import { useState, useEffect, useCallback } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { 
  Horse, Users, Home, Utensils, FileText, Camera, Send,
  Plus, RefreshCw, AlertTriangle, Calendar, Phone, Mail,
  MapPin, ClipboardList, DollarSign, Heart, Pill, Search,
  ChevronDown, ChevronUp, Edit2, Eye, Trash2, Check, X,
  TrendingUp, Clock, Filter, MoreVertical, Star, MessageSquare,
  Map, Fence, Save
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Horse icon component since lucide doesn't have a direct horse icon
const HorseIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 8c-1.5 1-3.5 1-5 0-1.5-1-3.5-1-5 0s-3.5 1-5 0c-1.5-1-3.5-1-5 0"/>
    <path d="M5.5 18.5 2 22"/>
    <path d="m18.5 18.5 3.5 3.5"/>
    <path d="M12 22v-8"/>
    <path d="M8 22V10"/>
    <path d="M16 22V10"/>
    <path d="M12 14c-2-2-2-4 0-6s4 0 4 2"/>
    <path d="M12 8V2l2 2"/>
  </svg>
);

export default function PensionEquestre() {
  const { activeSociete } = useWorkspace();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [dashboard, setDashboard] = useState({});
  const [chevaux, setChevaux] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [hebergements, setHebergements] = useState([]);
  const [contrats, setContrats] = useState([]);
  const [factures, setFactures] = useState([]);
  const [parcelles, setParcelles] = useState([]);
  const [parcellesEquines, setParcellesEquines] = useState({ parcelles: [], stats: {} });
  
  // Modal states
  const [showAddCheval, setShowAddCheval] = useState(false);
  const [showAddProspect, setShowAddProspect] = useState(false);
  const [showChevalDetail, setShowChevalDetail] = useState(null);
  const [showRapport, setShowRapport] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editChevalData, setEditChevalData] = useState(null);
  
  // Form states
  const [newCheval, setNewCheval] = useState({
    nom: '', sire: '', race: '', robe: '', sexe: 'hongre',
    proprietaire_nom: '', proprietaire_prenom: '', proprietaire_telephone: '', proprietaire_email: '',
    contact_urgence_nom: '', contact_urgence_telephone: '',
    veterinaire_nom: '', veterinaire_telephone: '',
    type_pension: 'box', box_numero: '', date_entree: new Date().toISOString().split('T')[0],
    tarif_mensuel: 350, tva_taux: 20, ration_foin_kg: 10, ration_granules_kg: 2
  });
  
  const [newProspect, setNewProspect] = useState({
    nom: '', prenom: '', telephone: '', email: '',
    cheval_nom: '', type_pension_souhaite: 'box', source: 'bouche_a_oreille', notes: ''
  });

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = { 
      'Authorization': `Bearer ${token}`,
      'X-Societe-Id': activeSociete?.id || ''
    };

    try {
      // Fetch dashboard
      const dashRes = await fetch(`${API_URL}/api/pension/dashboard`, { headers });
      if (dashRes.ok) {
        const data = await dashRes.json();
        setDashboard(data);
      }

      // Fetch chevaux
      const chevauxRes = await fetch(`${API_URL}/api/pension/chevaux`, { headers });
      if (chevauxRes.ok) {
        const data = await chevauxRes.json();
        setChevaux(data.chevaux || []);
      }

      // Fetch prospects
      const prospectsRes = await fetch(`${API_URL}/api/pension/prospects`, { headers });
      if (prospectsRes.ok) {
        const data = await prospectsRes.json();
        setProspects(data.prospects || []);
      }

      // Fetch hébergements
      const hebergementsRes = await fetch(`${API_URL}/api/pension/hebergements`, { headers });
      if (hebergementsRes.ok) {
        const data = await hebergementsRes.json();
        setHebergements(data.hebergements || []);
      }

      // Fetch contrats
      const contratsRes = await fetch(`${API_URL}/api/pension/contrats`, { headers });
      if (contratsRes.ok) {
        const data = await contratsRes.json();
        setContrats(data.contrats || []);
      }

      // Fetch factures
      const facturesRes = await fetch(`${API_URL}/api/pension/factures`, { headers });
      if (facturesRes.ok) {
        const data = await facturesRes.json();
        setFactures(data.factures || []);
      }

      // Fetch parcelles équines
      const parcellesRes = await fetch(`${API_URL}/api/pension/parcelles-equines`, { headers });
      if (parcellesRes.ok) {
        const data = await parcellesRes.json();
        setParcellesEquines(data);
      }
    } catch (error) {
      console.error('Erreur chargement données pension:', error);
    }
    setLoading(false);
  }, [activeSociete]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Create cheval
  const handleCreateCheval = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/pension/inscription-complete`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Societe-Id': activeSociete?.id || ''
        },
        body: JSON.stringify({
          ...newCheval,
          cheval_nom: newCheval.nom,
          cheval_sire: newCheval.sire,
          cheval_race: newCheval.race,
          cheval_robe: newCheval.robe,
          cheval_sexe: newCheval.sexe
        })
      });
      
      if (res.ok) {
        setShowAddCheval(false);
        setNewCheval({
          nom: '', sire: '', race: '', robe: '', sexe: 'hongre',
          proprietaire_nom: '', proprietaire_prenom: '', proprietaire_telephone: '', proprietaire_email: '',
          contact_urgence_nom: '', contact_urgence_telephone: '',
          veterinaire_nom: '', veterinaire_telephone: '',
          type_pension: 'box', box_numero: '', date_entree: new Date().toISOString().split('T')[0],
          tarif_mensuel: 350, tva_taux: 20, ration_foin_kg: 10, ration_granules_kg: 2
        });
        fetchData();
      }
    } catch (error) {
      console.error('Erreur création cheval:', error);
    }
  };

  // Create prospect
  const handleCreateProspect = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/pension/prospects`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Societe-Id': activeSociete?.id || ''
        },
        body: JSON.stringify(newProspect)
      });
      
      if (res.ok) {
        setShowAddProspect(false);
        setNewProspect({
          nom: '', prenom: '', telephone: '', email: '',
          cheval_nom: '', type_pension_souhaite: 'box', source: 'bouche_a_oreille', notes: ''
        });
        fetchData();
      }
    } catch (error) {
      console.error('Erreur création prospect:', error);
    }
  };

  // Update prospect status
  const handleUpdateProspectStatus = async (prospectId, newStatus) => {
    const token = localStorage.getItem('token');
    try {
      await fetch(`${API_URL}/api/pension/prospects/${prospectId}/statut?statut=${newStatus}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'X-Societe-Id': activeSociete?.id || ''
        }
      });
      fetchData();
    } catch (error) {
      console.error('Erreur mise à jour prospect:', error);
    }
  };

  const getStatutColor = (statut) => {
    switch (statut) {
      case 'nouveau': return 'bg-blue-100 text-blue-700';
      case 'contacte': return 'bg-yellow-100 text-yellow-700';
      case 'visite_planifiee': return 'bg-purple-100 text-purple-700';
      case 'visite_faite': return 'bg-indigo-100 text-indigo-700';
      case 'devis_envoye': return 'bg-orange-100 text-orange-700';
      case 'gagne': return 'bg-green-100 text-green-700';
      case 'perdu': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getStatutLabel = (statut) => {
    const labels = {
      'nouveau': 'Nouveau',
      'contacte': 'Contacté',
      'visite_planifiee': 'Visite planifiée',
      'visite_faite': 'Visite faite',
      'devis_envoye': 'Devis envoyé',
      'gagne': 'Gagné',
      'perdu': 'Perdu'
    };
    return labels[statut] || statut;
  };

  const tabs = [
    { id: 'dashboard', label: 'Tableau de bord', icon: TrendingUp },
    { id: 'chevaux', label: 'Chevaux', icon: HorseIcon, count: chevaux.length },
    { id: 'prospects', label: 'Prospects', icon: Users, count: prospects.filter(p => !['gagne', 'perdu'].includes(p.statut)).length },
    { id: 'hebergements', label: 'Boxes & Prés', icon: Home, count: hebergements.length },
    { id: 'parcelles', label: 'Parcelles', icon: Map, count: parcellesEquines.parcelles?.length || 0 },
    { id: 'contrats', label: 'Contrats', icon: FileText, count: contrats.length },
    { id: 'factures', label: 'Facturation', icon: DollarSign, count: factures.length },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-amber-600" />
        <span className="ml-3 text-gray-600">Chargement de la pension...</span>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="pension-equestre-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <HorseIcon className="w-6 h-6 text-amber-700" />
            </div>
            Gestion Pension Équestre
          </h1>
          <p className="text-gray-500 mt-1">Chevaux, propriétaires, boxes, facturation</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => setShowAddCheval(true)}
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
            data-testid="add-cheval-btn"
          >
            <Plus className="w-4 h-4" />
            Nouveau pensionnaire
          </button>
          <button 
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Stats Cards - Dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <HorseIcon className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Chevaux</p>
              <p className="text-xl font-bold">{dashboard.chevaux_actifs || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Home className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Boxes libres</p>
              <p className="text-xl font-bold">{dashboard.boxes_disponibles || 0}/{dashboard.boxes_total || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Prospects</p>
              <p className="text-xl font-bold">{dashboard.prospects_actifs || prospects.filter(p => !['gagne', 'perdu'].includes(p.statut)).length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <TrendingUp className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Taux occupation</p>
              <p className="text-xl font-bold">{dashboard.taux_occupation || 0}%</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Alertes alim.</p>
              <p className="text-xl font-bold">{dashboard.alertes_alimentation || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <DollarSign className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">CA mois</p>
              <p className="text-xl font-bold">{(dashboard.ca_mois || 0).toLocaleString()}€</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-amber-600 text-amber-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count !== undefined && tab.count > 0 && (
                <span className={`px-2 py-0.5 text-xs rounded-full ${
                  activeTab === tab.id ? 'bg-amber-100' : 'bg-gray-100'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl shadow-sm border">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="p-6 space-y-6">
            <h2 className="text-lg font-semibold">Aperçu de la pension</h2>
            
            {/* Alertes */}
            {(dashboard.alertes_alimentation > 0 || dashboard.vaccins_a_faire > 0 || dashboard.factures_retard > 0) && (
              <div className="space-y-3">
                {dashboard.alertes_alimentation > 0 && (
                  <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    <div>
                      <p className="font-medium text-red-800">{dashboard.alertes_alimentation} cheval(aux) avec problème d'appétit</p>
                      <p className="text-sm text-red-600">Vérifier les rations et l'état de santé</p>
                    </div>
                  </div>
                )}
                {dashboard.vaccins_a_faire > 0 && (
                  <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <Pill className="w-5 h-5 text-amber-600" />
                    <div>
                      <p className="font-medium text-amber-800">{dashboard.vaccins_a_faire} vaccin(s) à faire ce mois</p>
                      <p className="text-sm text-amber-600">Planifier les rendez-vous vétérinaires</p>
                    </div>
                  </div>
                )}
                {dashboard.factures_retard > 0 && (
                  <div className="flex items-center gap-3 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                    <DollarSign className="w-5 h-5 text-orange-600" />
                    <div>
                      <p className="font-medium text-orange-800">{dashboard.factures_retard} facture(s) en retard de paiement</p>
                      <p className="text-sm text-orange-600">Relancer les propriétaires concernés</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Quick actions */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <button 
                onClick={() => setShowAddCheval(true)}
                className="p-4 border rounded-lg hover:bg-amber-50 transition-colors text-left"
              >
                <Plus className="w-6 h-6 text-amber-600 mb-2" />
                <p className="font-medium">Nouvelle inscription</p>
                <p className="text-sm text-gray-500">Ajouter un pensionnaire</p>
              </button>
              <button 
                onClick={() => setShowAddProspect(true)}
                className="p-4 border rounded-lg hover:bg-blue-50 transition-colors text-left"
              >
                <Users className="w-6 h-6 text-blue-600 mb-2" />
                <p className="font-medium">Nouveau prospect</p>
                <p className="text-sm text-gray-500">Enregistrer un appel</p>
              </button>
              <button 
                onClick={() => setActiveTab('chevaux')}
                className="p-4 border rounded-lg hover:bg-green-50 transition-colors text-left"
              >
                <Utensils className="w-6 h-6 text-green-600 mb-2" />
                <p className="font-medium">Suivi alimentation</p>
                <p className="text-sm text-gray-500">Rations quotidiennes</p>
              </button>
              <button 
                onClick={() => setActiveTab('factures')}
                className="p-4 border rounded-lg hover:bg-purple-50 transition-colors text-left"
              >
                <FileText className="w-6 h-6 text-purple-600 mb-2" />
                <p className="font-medium">Générer factures</p>
                <p className="text-sm text-gray-500">Facturation mensuelle</p>
              </button>
            </div>

            {/* Recent activity - Derniers chevaux */}
            {chevaux.length > 0 && (
              <div>
                <h3 className="font-medium mb-3">Chevaux en pension</h3>
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {chevaux.slice(0, 6).map(cheval => (
                    <div 
                      key={cheval.id} 
                      className="p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => setShowChevalDetail(cheval)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                          <HorseIcon className="w-6 h-6 text-amber-700" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold">{cheval.nom}</h4>
                          <p className="text-sm text-gray-500">{cheval.race || 'Race non spécifiée'} - {cheval.robe}</p>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          cheval.type_pension === 'box' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                        }`}>
                          {cheval.type_pension === 'box' ? 'Box' : 'Pré'}
                        </span>
                      </div>
                      <div className="mt-3 text-sm text-gray-600">
                        <p><strong>Propriétaire:</strong> {cheval.proprietaire_prenom} {cheval.proprietaire_nom}</p>
                        <p><strong>Tarif:</strong> {cheval.tarif_mensuel}€/mois</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Chevaux Tab */}
        {activeTab === 'chevaux' && (
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Chevaux en pension</h2>
              <button 
                onClick={() => setShowAddCheval(true)}
                className="flex items-center gap-2 px-3 py-2 bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Nouveau pensionnaire
              </button>
            </div>
            
            {chevaux.length === 0 ? (
              <div className="text-center py-12">
                <HorseIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">Aucun cheval en pension pour le moment</p>
                <button 
                  onClick={() => setShowAddCheval(true)}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
                >
                  Ajouter le premier pensionnaire
                </button>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {chevaux.map(cheval => (
                  <div 
                    key={cheval.id} 
                    className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => setShowChevalDetail(cheval)}
                    data-testid={`cheval-card-${cheval.id}`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                          <HorseIcon className="w-6 h-6 text-amber-700" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">{cheval.nom}</h3>
                          <p className="text-sm text-gray-500">{cheval.race} - {cheval.robe}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        cheval.statut === 'actif' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {cheval.statut === 'actif' ? 'Actif' : 'Sorti'}
                      </span>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Users className="w-4 h-4" />
                        <span>{cheval.proprietaire_prenom} {cheval.proprietaire_nom}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Phone className="w-4 h-4" />
                        <span>{cheval.proprietaire_telephone}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Home className="w-4 h-4" />
                        <span>{cheval.type_pension === 'box' ? `Box ${cheval.box_numero || ''}` : `Pré ${cheval.pre_nom || ''}`}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <DollarSign className="w-4 h-4" />
                        <span>{cheval.tarif_mensuel}€/mois</span>
                      </div>
                    </div>
                    
                    {cheval.sire && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-xs text-gray-500">SIRE: {cheval.sire}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Prospects Tab */}
        {activeTab === 'prospects' && (
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Prospects Pension</h2>
              <button 
                onClick={() => setShowAddProspect(true)}
                className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                data-testid="add-prospect-btn"
              >
                <Plus className="w-4 h-4" />
                Nouveau prospect
              </button>
            </div>
            
            {prospects.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">Aucun prospect enregistré</p>
                <button 
                  onClick={() => setShowAddProspect(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Enregistrer un appel
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Contact</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Cheval</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Type souhaité</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Source</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Statut</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {prospects.map(prospect => (
                      <tr key={prospect.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium">{prospect.prenom} {prospect.nom}</p>
                            <p className="text-sm text-gray-500">{prospect.telephone}</p>
                            {prospect.email && <p className="text-sm text-gray-400">{prospect.email}</p>}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p>{prospect.cheval_nom || '-'}</p>
                          {prospect.cheval_race && <p className="text-sm text-gray-500">{prospect.cheval_race}</p>}
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 bg-amber-50 text-amber-700 text-xs rounded-full">
                            {prospect.type_pension_souhaite}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-sm">
                          {prospect.source?.replace(/_/g, ' ')}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatutColor(prospect.statut)}`}>
                            {getStatutLabel(prospect.statut)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <select 
                              className="text-sm border rounded px-2 py-1"
                              value={prospect.statut}
                              onChange={(e) => handleUpdateProspectStatus(prospect.id, e.target.value)}
                            >
                              <option value="nouveau">Nouveau</option>
                              <option value="contacte">Contacté</option>
                              <option value="visite_planifiee">Visite planifiée</option>
                              <option value="visite_faite">Visite faite</option>
                              <option value="devis_envoye">Devis envoyé</option>
                              <option value="gagne">Gagné</option>
                              <option value="perdu">Perdu</option>
                            </select>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Hébergements Tab */}
        {activeTab === 'hebergements' && (
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Boxes & Prés</h2>
              <button className="flex items-center gap-2 px-3 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors">
                <Plus className="w-4 h-4" />
                Ajouter hébergement
              </button>
            </div>
            
            {hebergements.length === 0 ? (
              <div className="text-center py-12">
                <Home className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Aucun hébergement configuré</p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                {hebergements.map(h => (
                  <div 
                    key={h.id} 
                    className={`border rounded-lg p-4 ${
                      h.statut === 'disponible' ? 'bg-green-50 border-green-200' : 
                      h.statut === 'occupe' ? 'bg-amber-50 border-amber-200' : 
                      'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold">{h.nom}</h3>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        h.statut === 'disponible' ? 'bg-green-100 text-green-700' :
                        h.statut === 'occupe' ? 'bg-amber-100 text-amber-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {h.statut === 'disponible' ? 'Libre' : h.statut === 'occupe' ? 'Occupé' : 'Maintenance'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{h.type_hebergement}</p>
                    {h.cheval_nom && (
                      <p className="text-sm font-medium text-amber-800">
                        <HorseIcon className="w-4 h-4 inline mr-1" />
                        {h.cheval_nom}
                      </p>
                    )}
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <p className="text-sm text-gray-500">{h.tarif_mensuel}€/mois</p>
                      {h.surface_m2 && <p className="text-xs text-gray-400">{h.surface_m2} m²</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Parcelles Tab */}
        {activeTab === 'parcelles' && (
          <div className="p-4" data-testid="parcelles-equines-tab">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Map className="w-5 h-5 text-emerald-600" />
                Parcelles Équines
              </h2>
              <div className="text-sm text-gray-500">
                {parcellesEquines.stats?.surface_totale_ha?.toFixed(1) || 0} ha total
              </div>
            </div>

            {/* Stats rapides */}
            {parcellesEquines.stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-emerald-50 rounded-lg p-4">
                  <p className="text-emerald-600 text-sm font-medium">Parcelles</p>
                  <p className="text-2xl font-bold text-emerald-700">{parcellesEquines.stats.total_parcelles || 0}</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-4">
                  <p className="text-amber-600 text-sm font-medium">Surface totale</p>
                  <p className="text-2xl font-bold text-amber-700">{parcellesEquines.stats.surface_totale_ha?.toFixed(2) || 0} ha</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-blue-600 text-sm font-medium">Pâturages</p>
                  <p className="text-2xl font-bold text-blue-700">{parcellesEquines.stats.par_usage?.paturage?.count || 0}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-purple-600 text-sm font-medium">Paddocks</p>
                  <p className="text-2xl font-bold text-purple-700">{parcellesEquines.stats.par_usage?.paddock?.count || 0}</p>
                </div>
              </div>
            )}
            
            {(!parcellesEquines.parcelles || parcellesEquines.parcelles.length === 0) ? (
              <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
                <Fence className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-2">Aucune parcelle équine définie</p>
                <p className="text-sm text-gray-400 mb-4">
                  Ajoutez des parcelles dédiées aux chevaux (pâturages, paddocks, carrières)
                </p>
                <button 
                  onClick={() => {/* TODO: modal ajout parcelle */}}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors mx-auto"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter une parcelle équine
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {parcellesEquines.parcelles.map(parcelle => (
                  <div 
                    key={parcelle.id} 
                    className="border rounded-xl p-4 hover:shadow-lg transition-all bg-white"
                    data-testid={`parcelle-card-${parcelle.id}`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-lg">{parcelle.nom}</h3>
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {parcelle.lieu_dit || parcelle.commune || 'Non localisée'}
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        parcelle.type_usage === 'paturage' ? 'bg-green-100 text-green-700' :
                        parcelle.type_usage === 'paddock' ? 'bg-amber-100 text-amber-700' :
                        parcelle.type_usage === 'carriere' ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {parcelle.type_usage || 'Équine'}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                      <div>
                        <p className="text-gray-500">Surface</p>
                        <p className="font-medium">{parcelle.surface_ha?.toFixed(2) || 0} ha</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Capacité</p>
                        <p className="font-medium">{parcelle.capacite_chevaux || '?'} chevaux</p>
                      </div>
                    </div>
                    
                    {parcelle.chevaux_presents && parcelle.chevaux_presents.length > 0 && (
                      <div className="border-t pt-3 mt-3">
                        <p className="text-xs text-gray-500 mb-2">Chevaux présents :</p>
                        <div className="flex flex-wrap gap-1">
                          {parcelle.chevaux_presents.map((cheval, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-amber-50 text-amber-700 text-xs rounded-full">
                              {cheval.nom || cheval}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {parcelle.etat_paturage && (
                      <div className="border-t pt-3 mt-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">État du pâturage</span>
                          <span className={`text-xs font-medium ${
                            parcelle.etat_paturage === 'excellent' ? 'text-green-600' :
                            parcelle.etat_paturage === 'bon' ? 'text-emerald-600' :
                            parcelle.etat_paturage === 'moyen' ? 'text-amber-600' :
                            'text-red-600'
                          }`}>
                            {parcelle.etat_paturage}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Contrats Tab */}
        {activeTab === 'contrats' && (
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Contrats de Pension</h2>
            </div>
            
            {contrats.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Aucun contrat enregistré</p>
              </div>
            ) : (
              <div className="space-y-4">
                {contrats.map(contrat => (
                  <div key={contrat.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold">{contrat.numero_contrat}</h3>
                        <p className="text-sm text-gray-600">{contrat.cheval_nom} - {contrat.proprietaire_nom}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        contrat.statut === 'actif' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {contrat.statut}
                      </span>
                    </div>
                    <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Type</p>
                        <p className="font-medium">{contrat.type_pension}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Tarif</p>
                        <p className="font-medium">{contrat.tarif_mensuel}€/mois</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Début</p>
                        <p className="font-medium">{contrat.date_debut}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Signatures</p>
                        <p className="font-medium">
                          {contrat.signe_client && contrat.signe_pension ? (
                            <span className="text-green-600">Complet</span>
                          ) : (
                            <span className="text-amber-600">En attente</span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Factures Tab */}
        {activeTab === 'factures' && (
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Facturation Pension</h2>
              <button className="flex items-center gap-2 px-3 py-2 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors">
                <Plus className="w-4 h-4" />
                Générer factures du mois
              </button>
            </div>
            
            {factures.length === 0 ? (
              <div className="text-center py-12">
                <DollarSign className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Aucune facture générée</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">N° Facture</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Cheval</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Période</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Montant TTC</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {factures.map(facture => (
                      <tr key={facture.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium">{facture.numero_facture}</td>
                        <td className="px-4 py-3">
                          <p>{facture.cheval_nom}</p>
                          <p className="text-sm text-gray-500">{facture.client_nom}</p>
                        </td>
                        <td className="px-4 py-3">{facture.periode}</td>
                        <td className="px-4 py-3 font-medium">{facture.total_ttc?.toFixed(2)}€</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            facture.statut === 'payee' ? 'bg-green-100 text-green-700' :
                            facture.statut === 'envoyee' ? 'bg-blue-100 text-blue-700' :
                            facture.statut === 'relancee' ? 'bg-orange-100 text-orange-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {facture.statut}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal: Nouveau Cheval */}
      {showAddCheval && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">Nouvelle inscription - Pensionnaire</h2>
                <button onClick={() => setShowAddCheval(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Infos cheval */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <HorseIcon className="w-5 h-5 text-amber-600" />
                  Informations du cheval
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                    <input 
                      type="text" 
                      className="w-full border rounded-lg px-3 py-2"
                      value={newCheval.nom}
                      onChange={(e) => setNewCheval({...newCheval, nom: e.target.value})}
                      placeholder="Nom du cheval"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">N° SIRE</label>
                    <input 
                      type="text" 
                      className="w-full border rounded-lg px-3 py-2"
                      value={newCheval.sire}
                      onChange={(e) => setNewCheval({...newCheval, sire: e.target.value})}
                      placeholder="Ex: 123456789A"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Race</label>
                    <input 
                      type="text" 
                      className="w-full border rounded-lg px-3 py-2"
                      value={newCheval.race}
                      onChange={(e) => setNewCheval({...newCheval, race: e.target.value})}
                      placeholder="Ex: Selle Français"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Robe</label>
                    <input 
                      type="text" 
                      className="w-full border rounded-lg px-3 py-2"
                      value={newCheval.robe}
                      onChange={(e) => setNewCheval({...newCheval, robe: e.target.value})}
                      placeholder="Ex: Bai, Alezan, Gris..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sexe</label>
                    <select 
                      className="w-full border rounded-lg px-3 py-2"
                      value={newCheval.sexe}
                      onChange={(e) => setNewCheval({...newCheval, sexe: e.target.value})}
                    >
                      <option value="hongre">Hongre</option>
                      <option value="jument">Jument</option>
                      <option value="etalon">Étalon</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Propriétaire */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  Propriétaire
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
                    <input 
                      type="text" 
                      className="w-full border rounded-lg px-3 py-2"
                      value={newCheval.proprietaire_prenom}
                      onChange={(e) => setNewCheval({...newCheval, proprietaire_prenom: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                    <input 
                      type="text" 
                      className="w-full border rounded-lg px-3 py-2"
                      value={newCheval.proprietaire_nom}
                      onChange={(e) => setNewCheval({...newCheval, proprietaire_nom: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone *</label>
                    <input 
                      type="tel" 
                      className="w-full border rounded-lg px-3 py-2"
                      value={newCheval.proprietaire_telephone}
                      onChange={(e) => setNewCheval({...newCheval, proprietaire_telephone: e.target.value})}
                      placeholder="06 XX XX XX XX"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input 
                      type="email" 
                      className="w-full border rounded-lg px-3 py-2"
                      value={newCheval.proprietaire_email}
                      onChange={(e) => setNewCheval({...newCheval, proprietaire_email: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* Contacts urgence */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Phone className="w-5 h-5 text-red-600" />
                  Contact d'urgence
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                    <input 
                      type="text" 
                      className="w-full border rounded-lg px-3 py-2"
                      value={newCheval.contact_urgence_nom}
                      onChange={(e) => setNewCheval({...newCheval, contact_urgence_nom: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                    <input 
                      type="tel" 
                      className="w-full border rounded-lg px-3 py-2"
                      value={newCheval.contact_urgence_telephone}
                      onChange={(e) => setNewCheval({...newCheval, contact_urgence_telephone: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* Vétérinaire */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Heart className="w-5 h-5 text-pink-600" />
                  Vétérinaire traitant
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom / Clinique</label>
                    <input 
                      type="text" 
                      className="w-full border rounded-lg px-3 py-2"
                      value={newCheval.veterinaire_nom}
                      onChange={(e) => setNewCheval({...newCheval, veterinaire_nom: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                    <input 
                      type="tel" 
                      className="w-full border rounded-lg px-3 py-2"
                      value={newCheval.veterinaire_telephone}
                      onChange={(e) => setNewCheval({...newCheval, veterinaire_telephone: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* Pension */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Home className="w-5 h-5 text-green-600" />
                  Pension
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type de pension</label>
                    <select 
                      className="w-full border rounded-lg px-3 py-2"
                      value={newCheval.type_pension}
                      onChange={(e) => setNewCheval({...newCheval, type_pension: e.target.value})}
                    >
                      <option value="box">Box</option>
                      <option value="pre">Pré</option>
                      <option value="box_pre">Box + Pré</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">N° Box / Nom pré</label>
                    <input 
                      type="text" 
                      className="w-full border rounded-lg px-3 py-2"
                      value={newCheval.box_numero}
                      onChange={(e) => setNewCheval({...newCheval, box_numero: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date d'entrée</label>
                    <input 
                      type="date" 
                      className="w-full border rounded-lg px-3 py-2"
                      value={newCheval.date_entree}
                      onChange={(e) => setNewCheval({...newCheval, date_entree: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tarif mensuel (€)</label>
                    <input 
                      type="number" 
                      className="w-full border rounded-lg px-3 py-2"
                      value={newCheval.tarif_mensuel}
                      onChange={(e) => setNewCheval({...newCheval, tarif_mensuel: parseFloat(e.target.value)})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Taux TVA (%)</label>
                    <select
                      className="w-full border rounded-lg px-3 py-2"
                      value={newCheval.tva_taux ?? 20}
                      onChange={(e) => setNewCheval({...newCheval, tva_taux: parseFloat(e.target.value)})}
                      data-testid="new-cheval-tva"
                    >
                      <option value={20}>20% - Taux normal</option>
                      <option value={10}>10% - Taux intermédiaire</option>
                      <option value={5.5}>5,5% - Taux réduit</option>
                      <option value={2.1}>2,1% - Super réduit</option>
                      <option value={0}>0% - Exonéré (franchise / particulier)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Alimentation */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Utensils className="w-5 h-5 text-orange-600" />
                  Alimentation
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Foin (kg/jour)</label>
                    <input 
                      type="number" 
                      step="0.5"
                      className="w-full border rounded-lg px-3 py-2"
                      value={newCheval.ration_foin_kg}
                      onChange={(e) => setNewCheval({...newCheval, ration_foin_kg: parseFloat(e.target.value)})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Granulés (kg/jour)</label>
                    <input 
                      type="number" 
                      step="0.5"
                      className="w-full border rounded-lg px-3 py-2"
                      value={newCheval.ration_granules_kg}
                      onChange={(e) => setNewCheval({...newCheval, ration_granules_kg: parseFloat(e.target.value)})}
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t bg-gray-50 flex justify-end gap-3 sticky bottom-0">
              <button 
                onClick={() => setShowAddCheval(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg"
              >
                Annuler
              </button>
              <button 
                onClick={handleCreateCheval}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
                data-testid="submit-cheval-btn"
              >
                Inscrire le pensionnaire
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Nouveau Prospect */}
      {showAddProspect && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">Nouveau prospect</h2>
                <button onClick={() => setShowAddProspect(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
                  <input 
                    type="text" 
                    className="w-full border rounded-lg px-3 py-2"
                    value={newProspect.prenom}
                    onChange={(e) => setNewProspect({...newProspect, prenom: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                  <input 
                    type="text" 
                    className="w-full border rounded-lg px-3 py-2"
                    value={newProspect.nom}
                    onChange={(e) => setNewProspect({...newProspect, nom: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone *</label>
                <input 
                  type="tel" 
                  className="w-full border rounded-lg px-3 py-2"
                  value={newProspect.telephone}
                  onChange={(e) => setNewProspect({...newProspect, telephone: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input 
                  type="email" 
                  className="w-full border rounded-lg px-3 py-2"
                  value={newProspect.email}
                  onChange={(e) => setNewProspect({...newProspect, email: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom du cheval</label>
                <input 
                  type="text" 
                  className="w-full border rounded-lg px-3 py-2"
                  value={newProspect.cheval_nom}
                  onChange={(e) => setNewProspect({...newProspect, cheval_nom: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type souhaité</label>
                  <select 
                    className="w-full border rounded-lg px-3 py-2"
                    value={newProspect.type_pension_souhaite}
                    onChange={(e) => setNewProspect({...newProspect, type_pension_souhaite: e.target.value})}
                  >
                    <option value="box">Box</option>
                    <option value="pre">Pré</option>
                    <option value="box_pre">Box + Pré</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                  <select 
                    className="w-full border rounded-lg px-3 py-2"
                    value={newProspect.source}
                    onChange={(e) => setNewProspect({...newProspect, source: e.target.value})}
                  >
                    <option value="bouche_a_oreille">Bouche à oreille</option>
                    <option value="site_web">Site web</option>
                    <option value="reseaux_sociaux">Réseaux sociaux</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea 
                  className="w-full border rounded-lg px-3 py-2"
                  rows={3}
                  value={newProspect.notes}
                  onChange={(e) => setNewProspect({...newProspect, notes: e.target.value})}
                  placeholder="Informations complémentaires..."
                />
              </div>
            </div>
            
            <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
              <button 
                onClick={() => setShowAddProspect(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg"
              >
                Annuler
              </button>
              <button 
                onClick={handleCreateProspect}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                data-testid="submit-prospect-btn"
              >
                Enregistrer le prospect
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Détail Cheval */}
      {showChevalDetail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                    <HorseIcon className="w-6 h-6 text-amber-700" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">{showChevalDetail.nom}</h2>
                    <p className="text-sm text-gray-500">{showChevalDetail.race} - {showChevalDetail.robe}</p>
                  </div>
                </div>
                <button onClick={() => setShowChevalDetail(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Informations générales */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Identification</h4>
                  <p className="text-sm"><strong>SIRE:</strong> {showChevalDetail.sire || 'Non renseigné'}</p>
                  {editMode ? (
                    <div className="space-y-2 mt-2">
                      <div>
                        <label className="text-xs font-medium">Nom</label>
                        <input
                          type="text" value={editChevalData?.nom || ''}
                          onChange={(e) => setEditChevalData({ ...editChevalData, nom: e.target.value })}
                          className="w-full px-2 py-1 border rounded text-sm"
                          data-testid="edit-cheval-nom"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium">Race</label>
                        <input
                          type="text" value={editChevalData?.race || ''}
                          onChange={(e) => setEditChevalData({ ...editChevalData, race: e.target.value })}
                          className="w-full px-2 py-1 border rounded text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium">Sexe</label>
                        <select value={editChevalData?.sexe || 'hongre'}
                          onChange={(e) => setEditChevalData({ ...editChevalData, sexe: e.target.value })}
                          className="w-full px-2 py-1 border rounded text-sm">
                          <option value="hongre">Hongre</option>
                          <option value="jument">Jument</option>
                          <option value="etalon">Étalon</option>
                          <option value="poulain">Poulain</option>
                        </select>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm"><strong>Sexe:</strong> {showChevalDetail.sexe}</p>
                      <p className="text-sm"><strong>Date d'entrée:</strong> {showChevalDetail.date_entree}</p>
                    </>
                  )}
                </div>
                <div className="p-4 bg-amber-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Pension</h4>
                  {editMode ? (
                    <div className="space-y-2">
                      <div>
                        <label className="text-xs font-medium">Type de pension</label>
                        <select value={editChevalData?.type_pension || 'box'}
                          onChange={(e) => setEditChevalData({ ...editChevalData, type_pension: e.target.value })}
                          className="w-full px-2 py-1 border rounded text-sm">
                          <option value="box">Box</option>
                          <option value="pre">Pré</option>
                          <option value="box_pre">Box + Pré</option>
                          <option value="travail">Travail</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-medium">N° Box / Emplacement</label>
                        <input type="text" value={editChevalData?.box_numero || ''}
                          onChange={(e) => setEditChevalData({ ...editChevalData, box_numero: e.target.value })}
                          className="w-full px-2 py-1 border rounded text-sm" />
                      </div>
                      <div>
                        <label className="text-xs font-medium">💰 Tarif mensuel (€)</label>
                        <input type="number" step="10" value={editChevalData?.tarif_mensuel || 0}
                          onChange={(e) => setEditChevalData({ ...editChevalData, tarif_mensuel: parseFloat(e.target.value) || 0 })}
                          className="w-full px-2 py-1 border rounded text-sm font-bold"
                          data-testid="edit-cheval-tarif" />
                      </div>
                      <div>
                        <label className="text-xs font-medium">📊 Taux TVA (%)</label>
                        <select
                          value={editChevalData?.tva_taux ?? 20}
                          onChange={(e) => setEditChevalData({ ...editChevalData, tva_taux: parseFloat(e.target.value) })}
                          className="w-full px-2 py-1 border rounded text-sm"
                          data-testid="edit-cheval-tva"
                        >
                          <option value={20}>20% - Taux normal</option>
                          <option value={10}>10% - Taux intermédiaire</option>
                          <option value={5.5}>5,5% - Taux réduit</option>
                          <option value={2.1}>2,1% - Super réduit</option>
                          <option value={0}>0% - Exonéré (franchise / particulier)</option>
                        </select>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm"><strong>Type:</strong> {showChevalDetail.type_pension}</p>
                      <p className="text-sm"><strong>Emplacement:</strong> {showChevalDetail.box_numero || showChevalDetail.pre_nom || 'Non assigné'}</p>
                      <p className="text-sm"><strong>Tarif:</strong> {showChevalDetail.tarif_mensuel}€/mois</p>
                      <p className="text-sm"><strong>TVA:</strong> {showChevalDetail.tva_taux ?? 20}%</p>
                    </>
                  )}
                </div>
              </div>

              {/* Propriétaire */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Propriétaire
                </h4>
                <p className="text-sm"><strong>Nom:</strong> {showChevalDetail.proprietaire_prenom} {showChevalDetail.proprietaire_nom}</p>
                <p className="text-sm"><strong>Téléphone:</strong> {showChevalDetail.proprietaire_telephone}</p>
                <p className="text-sm"><strong>Email:</strong> {showChevalDetail.proprietaire_email || 'Non renseigné'}</p>
              </div>

              {/* Contacts */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-red-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Contact urgence
                  </h4>
                  <p className="text-sm">{showChevalDetail.contact_urgence_nom || 'Non renseigné'}</p>
                  <p className="text-sm">{showChevalDetail.contact_urgence_telephone || '-'}</p>
                </div>
                <div className="p-4 bg-pink-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                    <Heart className="w-4 h-4" />
                    Vétérinaire
                  </h4>
                  <p className="text-sm">{showChevalDetail.veterinaire_nom || 'Non renseigné'}</p>
                  <p className="text-sm">{showChevalDetail.veterinaire_telephone || '-'}</p>
                </div>
              </div>

              {/* Alimentation */}
              <div className="p-4 bg-green-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                  <Utensils className="w-4 h-4" />
                  Alimentation
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <p><strong>Foin:</strong> {showChevalDetail.ration_foin_kg || 10} kg/jour</p>
                  <p><strong>Granulés:</strong> {showChevalDetail.ration_granules_kg || 2} kg/jour</p>
                  {showChevalDetail.allergies && (
                    <p className="col-span-2 text-red-600"><strong>Allergies:</strong> {showChevalDetail.allergies}</p>
                  )}
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t bg-gray-50 flex justify-between sticky bottom-0">
              <button 
                onClick={async () => {
                  if (!window.confirm(`Confirmer la sortie de ${showChevalDetail.nom} de la pension ?`)) return;
                  try {
                    const token = localStorage.getItem('token');
                    const res = await fetch(`${API_URL}/api/pension/chevaux/${showChevalDetail.id}/sortie`, {
                      method: 'POST',
                      headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                        'X-Societe-Id': activeSociete?.id || ''
                      },
                      body: JSON.stringify({})
                    });
                    if (res.ok) {
                      toast.success(`${showChevalDetail.nom} sorti de la pension`);
                      setShowChevalDetail(null);
                      fetchData();
                    } else {
                      toast.error('Erreur sortie pensionnaire');
                    }
                  } catch (e) { toast.error('Erreur réseau'); }
                }}
                className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-2"
                data-testid="btn-sortie-pensionnaire"
              >
                <Trash2 className="w-4 h-4" />
                Sortie du pensionnaire
              </button>
              <div className="flex gap-3">
                <button 
                  onClick={() => setShowRapport(showChevalDetail)}
                  className="px-4 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 flex items-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  Envoyer rapport
                </button>
                {editMode ? (
                  <>
                    <button
                      onClick={() => { setEditMode(false); setEditChevalData(null); }}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                      data-testid="btn-cancel-edit-cheval"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('token');
                          const res = await fetch(`${API_URL}/api/pension/chevaux/${showChevalDetail.id}`, {
                            method: 'PUT',
                            headers: {
                              'Authorization': `Bearer ${token}`,
                              'Content-Type': 'application/json',
                              'X-Societe-Id': activeSociete?.id || ''
                            },
                            body: JSON.stringify(editChevalData)
                          });
                          if (res.ok) {
                            toast.success('Cheval modifié');
                            setShowChevalDetail({ ...showChevalDetail, ...editChevalData });
                            setEditMode(false);
                            setEditChevalData(null);
                            fetchData();
                          } else {
                            const err = await res.json();
                            toast.error(err.detail || 'Erreur modification');
                          }
                        } catch (e) { toast.error('Erreur réseau'); }
                      }}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                      data-testid="btn-save-cheval"
                    >
                      <Save className="w-4 h-4" />
                      Enregistrer
                    </button>
                  </>
                ) : (
                  <button 
                    onClick={() => {
                      setEditMode(true);
                      setEditChevalData({
                        nom: showChevalDetail.nom,
                        race: showChevalDetail.race,
                        robe: showChevalDetail.robe,
                        sexe: showChevalDetail.sexe,
                        proprietaire_nom: showChevalDetail.proprietaire_nom,
                        proprietaire_prenom: showChevalDetail.proprietaire_prenom,
                        proprietaire_telephone: showChevalDetail.proprietaire_telephone,
                        proprietaire_email: showChevalDetail.proprietaire_email,
                        type_pension: showChevalDetail.type_pension,
                        box_numero: showChevalDetail.box_numero || '',
                        tarif_mensuel: showChevalDetail.tarif_mensuel,
                        ration_foin_kg: showChevalDetail.ration_foin_kg || 10,
                        ration_granules_kg: showChevalDetail.ration_granules_kg || 2,
                        contact_urgence_nom: showChevalDetail.contact_urgence_nom || '',
                        contact_urgence_telephone: showChevalDetail.contact_urgence_telephone || '',
                        veterinaire_nom: showChevalDetail.veterinaire_nom || '',
                        veterinaire_telephone: showChevalDetail.veterinaire_telephone || '',
                        allergies: showChevalDetail.allergies || '',
                      });
                    }}
                    className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2"
                    data-testid="btn-modifier-cheval"
                  >
                    <Edit2 className="w-4 h-4" />
                    Modifier
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
